import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Clock as ClockIcon, Info as InfoIcon } from "@tamagui/lucide-icons";
import { InsuranceCheckProblem } from "@medbillai/generated-graphql-types";
import { AlertBar, isWeb, useFocusEffect, withViewTransition, YStack } from "@medbillai/ui";
import { useRouter } from "@medbillai/navigation/router";
import { coverageUrl, coverageWhereYouStandUrl } from "@medbillai/utils/urls";
import { useStartAccountLink } from "../../../lib/accounts-link/utils";
import { analyticsService } from "../../../lib/analytics";
import type { CoverageRootState, EventTypes } from "../../../lib/analytics/types";
import { markOpenedInApp } from "../../../lib/openedInApp";
import { coverageNotSupportedStorage } from "../../../lib/storage";
import { useCoveragePlanStore } from "../coveragePlanStore";
import { shortDate } from "../format";
import { MovementBanner, useMovementLink } from "../movement/MovementBanner";
import type { CoveragePlan, CoverageRead, PlanSummary } from "../read/readCoverage";
import { BillsClaimsSection } from "./BillsClaimsSection";
import { CoverageStateCard, notSupportedCopy } from "./CoverageStateCard";
import { DetailsRecheck } from "./FixDetailsCard";
import { HeadlineCard, headlineState } from "./HeadlineCard";
import { PlanHeader } from "./PlanHeader";
import { PlanSwitcher } from "./PlanSwitcher";
import { RootTiles } from "./RootTiles";
import { UninsuredSetup } from "./SetupCard";
import { SetupSheet } from "./SetupSheet";
import { type PlanCheckProps, type PlanSetup, usePlanCheck, usePlanSetup } from "./usePlanSetup";
export function CoverageRoot({
  read
}: {
  read: CoverageRead;
}) {
  const setup = usePlanSetup();
  const linkInsurance = useLinkInsurance();
  const checkingSince = useCoveragePlanStore(state => state.checkingSince);
  const showsTotals = read.kind === "plan" && headlineState(read.plan).kind === "totals";
  return <>
      <RootBody read={read} setup={setup} data-sentry-element="RootBody" data-sentry-source-file="CoverageRoot.tsx" />
      <BillsClaimsSection showsTotals={showsTotals} onLinkInsurance={() => linkInsurance("noRecords")} data-sentry-element="BillsClaimsSection" data-sentry-source-file="CoverageRoot.tsx" />
      <SetupSheet setup={setup} data-sentry-element="SetupSheet" data-sentry-source-file="CoverageRoot.tsx" />
      {setup.polling ? <SetupRun answeredBefore={setup.answeredBefore} attemptedBefore={setup.attemptedBefore} onDone={setup.resolve} /> : null}
      {checkingSince === undefined ? null : <DetailsRecheck key={checkingSince} since={checkingSince} />}
    </>;
}
function SetupRun(props: PlanCheckProps) {
  usePlanCheck(props);
  return null;
}
function RootBody({
  read,
  setup
}: {
  read: CoverageRead;
  setup: PlanSetup;
}) {
  // A pick from the uninsured sheet moves the read to NOT_STARTED, then
  // CHECKING, before its plan lands; the sheet's check keeps the root.
  const [fromUninsured, setFromUninsured] = useState(read.kind === "uninsured");
  useEffect(() => {
    if (read.kind === "uninsured") {
      setFromUninsured(true);
    } else if (read.kind === "setup" ? read.missing.length > 0 : read.kind !== "checking") {
      setFromUninsured(false);
    }
  }, [read]);
  const link = useMovementLink(read);
  const state = rootState(read);
  useEffect(() => {
    analyticsService.trackEvent("CoverageScreenViewed", {
      screen: "root",
      state
    });
  }, [state]);
  if (read.kind === "plan") {
    return <PlanFrame plan={read.plan} otherPlans={read.otherPlans} setup={setup}>
        <PlanBody plan={read.plan} linked={link.linked === read.plan.membershipId} onLinkOpened={link.opened} />
      </PlanFrame>;
  }
  // The notice replaces the figures, but the header still reaches other plans.
  if (read.kind === "failing" && read.plan) {
    const {
      confirmedAt
    } = read.plan;
    const keepsFigures = confirmedAt !== null && headlineState(read.plan).kind === "totals";
    return <PlanFrame plan={read.plan} otherPlans={read.otherPlans} badgeless={keepsFigures} setup={setup}>
        <YStack marginTop="$sp1" gap="$sp1.5">
          {keepsFigures ? <>
              <AlertBar icon={ClockIcon} message={checkFailedMessage(read.plan.payerName, confirmedAt, read.problem)} />
              <LastFigures plan={read.plan} />
            </> : <>
              {confirmedAt === null && read.problem === InsuranceCheckProblem.INSURER_NOT_SUPPORTED ? <NotSupportedOnce key={read.plan.membershipId} plan={read.plan}>
                  <CoverageStateCard read={read} setup={setup} />
                </NotSupportedOnce> : <CoverageStateCard read={read} setup={setup} />}
              {read.plan.headline ? <LastFigures plan={read.plan} /> : null}
            </>}
        </YStack>
      </PlanFrame>;
  }
  if (read.kind === "uninsured" || fromUninsured && (read.kind === "setup" || setup.checking)) {
    return <UninsuredSetup setup={setup} />;
  }
  return <CoverageStateCard read={read} setup={setup} data-sentry-element="CoverageStateCard" data-sentry-component="RootBody" data-sentry-source-file="CoverageRoot.tsx" />;
}
function PlanFrame({
  plan,
  otherPlans,
  badgeless,
  setup,
  children
}: {
  plan: CoveragePlan;
  otherPlans: PlanSummary[];
  badgeless?: boolean;
  setup: PlanSetup;
  children: ReactNode;
}) {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // On the web the setup sheet opens over the switcher, which closes with it:
  // closing it first shows the page between two full-screen dialogs.
  useEffect(() => {
    if (!setup.sheet.open) {
      setSwitcherOpen(false);
    }
  }, [setup.sheet.open]);
  return <YStack gap="$sp1.5" data-sentry-element="YStack" data-sentry-component="PlanFrame" data-sentry-source-file="CoverageRoot.tsx">
      <PlanHeader plan={plan} badgeless={badgeless} onPress={() => {
      analyticsService.trackEvent("CoverageSwitcherOpened", {});
      setSwitcherOpen(true);
    }} data-sentry-element="PlanHeader" data-sentry-source-file="CoverageRoot.tsx" />
      {children}
      <PlanSwitcher open={switcherOpen} onOpenChange={setSwitcherOpen} current={plan} others={otherPlans} onAddPlan={() => {
      setup.openSheet();
      if (!isWeb) {
        setSwitcherOpen(false);
      }
    }} data-sentry-element="PlanSwitcher" data-sentry-source-file="CoverageRoot.tsx" />
    </YStack>;
}
function PlanBody({
  plan,
  linked,
  onLinkOpened
}: {
  plan: CoveragePlan;
  linked: boolean;
  onLinkOpened: () => void;
}) {
  const linkInsurance = useLinkInsurance();
  const [today] = useState(() => new Date());
  return <>
      <YStack marginTop="$sp1" data-sentry-element="YStack" data-sentry-source-file="CoverageRoot.tsx">
        <MovementBanner plan={plan} linked={linked} onLinkOpened={onLinkOpened} data-sentry-element="MovementBanner" data-sentry-source-file="CoverageRoot.tsx" />
        <HeadlineCard plan={plan} today={today} onLinkInsurance={linkInsurance} onOpen={useOpenWhereYouStand()} data-sentry-element="HeadlineCard" data-sentry-source-file="CoverageRoot.tsx" />
      </YStack>
      <RootTiles plan={plan} data-sentry-element="RootTiles" data-sentry-source-file="CoverageRoot.tsx" />
    </>;
}

/** The full card on the first visit; nothing the user does changes it, so a bar after. */
function NotSupportedOnce({
  plan,
  children
}: {
  plan: CoveragePlan;
  children: ReactNode;
}) {
  const [seen, setSeen] = useState(() => coverageNotSupportedStorage.get(plan.membershipId) !== null);
  // A native tab stays mounted, so each return to it counts as a visit.
  useFocusEffect(useCallback(() => {
    setSeen(coverageNotSupportedStorage.get(plan.membershipId) !== null);
    coverageNotSupportedStorage.set(plan.membershipId, "seen");
  }, [plan.membershipId]));
  return seen ? <AlertBar icon={InfoIcon} message={notSupportedCopy(plan.payerName)} data-sentry-element="AlertBar" data-sentry-component="NotSupportedOnce" data-sentry-source-file="CoverageRoot.tsx" /> : children;
}
const checkFailedMessage = (payer: string, confirmedAt: Date, problem: InsuranceCheckProblem | null) => {
  const from = `We’ll check again automatically. Showing figures from ${shortDate(confirmedAt)}.`;
  return problem === InsuranceCheckProblem.INSURER_UNAVAILABLE ? `${payer} isn’t answering right now. ${from}` : `We couldn’t check your plan with ${payer} this time. ${from}`;
};
function LastFigures({
  plan
}: {
  plan: CoveragePlan;
}) {
  const [today] = useState(() => new Date());
  return <HeadlineCard plan={plan} today={today} onLinkInsurance={useLinkInsurance()} onOpen={useOpenWhereYouStand()} data-sentry-element="HeadlineCard" data-sentry-component="LastFigures" data-sentry-source-file="CoverageRoot.tsx" />;
}
function useOpenWhereYouStand() {
  const {
    push
  } = useRouter();
  return () => {
    analyticsService.trackEvent("CoverageWhereYouStandOpened", {
      from: "headline"
    });
    markOpenedInApp(coverageWhereYouStandUrl());
    withViewTransition("hero-shell", () => push(coverageWhereYouStandUrl()));
  };
}
function useLinkInsurance() {
  const startAccountLink = useStartAccountLink();
  return (source: EventTypes["CoverageLinkInsuranceTapped"]["source"]) => {
    analyticsService.trackEvent("CoverageLinkInsuranceTapped", {
      source
    });
    const startedFrom = `coverage_${source}`;
    analyticsService.trackEvent("InsuranceLinkingStarted", {
      source: startedFrom
    });
    startAccountLink({
      exitUrl: coverageUrl(),
      startedFrom
    });
  };
}
const rootState = (read: CoverageRead): CoverageRootState => read.kind === "plan" ? planState(read.plan) : read.kind === "failing" ? "couldntConfirm" : read.kind;
const planState = (plan: CoveragePlan): CoverageRootState => {
  const {
    kind
  } = headlineState(plan);
  return kind !== "totals" ? kind : plan.rootPrice ? "totalsAndPrices" : "totalsOnly";
};